import { useHistory } from 'react-router-dom'
import { Icon } from '@iconify/react'
import clsx from 'clsx'
import { useGlobalState } from '@pepper/ui'
import noop from '@pepper/utils/src/function/noop'

import ProjectGridCard from 'components/Card/ProjectGridCard'
import { useProjectDomainMap } from 'utils/hooks/useProjectDomainMap'
import { useSelectedCompanyId } from 'utils/functions/misc'
import emptyRecentProject from 'assets/images/projects_empty_state.png'
import { RecentProjectsSkeleton } from '../Skeletons/HomPageSkeletons'

import s from './RecentProjects.module.css'

type Props = {
  projects: Project[]
  isProjectsLoading: boolean
  totalProjects: number
  setShowProjectModal: React.Dispatch<React.SetStateAction<boolean>>
}

const RecentProjects = ({
  projects,
  isProjectsLoading,
  totalProjects,
  setShowProjectModal,
}: Props): JSX.Element => {
  const history = useHistory()
  const companyId = useSelectedCompanyId()
  const { domainMap } = useProjectDomainMap({
    companyId,
  })
  const { permissions } = useGlobalState()

  const handleProjectCardClick = (record: Project | ProjectTemplateList) => {
    const tab = permissions?.cmp ? 'topics' : 'items'

    const search =
      tab === 'items' && !permissions?.marketplace
        ? `?item_types=private_doc`
        : undefined

    history.push({
      pathname: `/project/${record.id}/${tab}`,
      search,
    })
  }

  if (isProjectsLoading) {
    return <RecentProjectsSkeleton />
  }

  return (
    <div className={s.wrapper}>
      {totalProjects > 0 ? (
        <div className={s.liveProjectCardBlock}>
          {projects?.map((project, index) => {
            const domain = domainMap?.[project.id]?.[0]?.domain
            return (
              <div
                key={index}
                onClick={() => handleProjectCardClick(project)}
                onKeyPress={noop}
                role="button"
                tabIndex={0}
              >
                <ProjectGridCard
                  project={project}
                  stage_count={project.stage_count ?? {}}
                  has_pending_draft_items={false}
                  isDraft={false}
                  isPaidDraft={false}
                  domain={domain}
                  number_of_items={project.number_of_items}
                />
              </div>
            )
          })}
        </div>
      ) : (
        <div className={s.emptyState}>
          <img
            src={emptyRecentProject}
            alt="Recent Projects"
            width="350"
            height="104"
          />
          <span className={s.emptyStateText}>
            Group all your related content in a project for easy access.{' '}
          </span>
          <button
            className={clsx(s.redirectLink, s.createNewProjectCta)}
            onClick={() => setShowProjectModal(true)}
          >
            <Icon icon="lucide:plus" width={14} /> New Project
          </button>
        </div>
      )}
    </div>
  )
}

export default RecentProjects
